import { SCRIPT_TYPE, partytownSnippet } from 'partytown-wix-fork/integration'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { BrowserWindow, BrowserWindowSymbol, Fetch, IFetchApi, SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { name } from '../symbols'
import { getPartytownConfig } from '../utils/partyTownLoader'
import { ExternalComponentSiteConfig, IPartytownLoader } from '../types'
import { getExternalComponentRendererUrl } from '../utils/getExternalComponentRendererUrl'

const addScriptTag = (window: Window, { type, innerHTML, src }: { type: string; innerHTML?: string; src?: string }) => {
	const scriptElement = window.document.createElement('script')
	scriptElement.type = type
	if (innerHTML) {
		scriptElement.innerHTML = innerHTML
	}

	if (src) {
		scriptElement.src = src
	}

	window.document.head.appendChild(scriptElement)
}

const getLocalRendererUrl = async () => {
	if (process.env.NODE_ENV === 'development') {
		const fileContent = (
			await import(
				// @ts-ignore - load the file as text in order to use it as a data url
				'raw-loader!@wix/external-component-renderer/dist/statics/external-component-renderer.umd.js'
			)
		).default

		// convert the content into a base64 data url
		return `data:application/javascript;base64,${Buffer.from(fileContent).toString('base64')}`
	}

	return ''
}

export const EditorPartytownLoader = withDependencies(
	[BrowserWindowSymbol, named(SiteFeatureConfigSymbol, name), Fetch],
	(
		window: NonNullable<BrowserWindow>,
		{ staticServerUrl }: ExternalComponentSiteConfig,
		fetchApi: IFetchApi
	): IPartytownLoader => {
		let partytownPromise: Promise<void> | null = null

		const load = async () => {
			const isDebug = false

			const snippet = partytownSnippet(getPartytownConfig(isDebug))

			if (!snippet.endsWith('(window,document,navigator,top,window.crossOriginIsolated);')) {
				throw new Error('Unexpected Partytown snippet')
			}

			const partytownReady = new Promise((resolve) => window.document.addEventListener('pt0', resolve))

			const partytownScript = snippet.replace(
				'(window,document,navigator,top,window.crossOriginIsolated);',
				'(window,document,navigator,window,window.crossOriginIsolated);'
			)

			addScriptTag(window, { type: 'text/javascript', innerHTML: partytownScript })

			if (process.env.NODE_ENV === 'development') {
				const src = await getLocalRendererUrl()
				addScriptTag(window, { type: SCRIPT_TYPE, src })
			} else {
				const externalRendererUrl = getExternalComponentRendererUrl(staticServerUrl)
				const externalRendererScript = await fetchApi.envFetch(externalRendererUrl).then((res) => res.text())

				addScriptTag(window, { type: SCRIPT_TYPE, innerHTML: externalRendererScript })
			}

			window.dispatchEvent(new CustomEvent('ptupdate'))

			await partytownReady
		}

		return {
			async loadPartytown() {
				partytownPromise = partytownPromise || load()

				return partytownPromise
			},
		}
	}
)
